import { Component, OnInit } from "@angular/core"
import { Router } from "@angular/router"
import { SessionService } from "src/app/services/session.service"
import { toastrService } from "src/app/services/ux/toastr.service"
import { Client } from "src/app/services/API/client"
import { LocaleConverterService } from "src/app/common/helpers/locale-converter.service"
import { TranslateService } from "@ngx-translate/core"
import { DropdownOption, Document, LoanPurposeDocument, LoanEnquireRequest } from "src/app/models/loan-enquire/loan-enquire"

const FILE_SIZE_LIMIT = 10000000
const DOCUMENT_OTHER_TYPE_ID = 4
const fileInputId: string = "file-input"

@Component({
  templateUrl: "./create-loan-enquire.component.html",
  styleUrls: ["./create-loan-enquire.component.css"]
})

export class CreateLoanEnquireComponent implements OnInit {
  loading: boolean = false
  loanPurposeOptions: DropdownOption[] = []
  currencyOptions: DropdownOption[] = [
    { value: "ALL", label: "ALL" },
    { value: "EUR", label: "EUR" },
    { value: "XCD", label: "XCD" }
  ]
  sectorOptions: DropdownOption[] = []
  subSectorOptions: DropdownOption[] = []
  sex: string = "M"
  disbursmentDate: string = ""
  status: string = ""
  loanPurposeId: number = 0
  sectorId: number = 0
  subSectorId: number = 0
  currencyCode: string = "ALL"
  amount: number = 0
  loanPurposeDocuments: LoanPurposeDocument[] = []
  enquireDocuments: (Document | null)[] = []

  constructor(
    public toastr: toastrService,
    private session: SessionService,
    private api: Client,
    private converter: LocaleConverterService,
    private router: Router,
    private translateService: TranslateService
  ) {
  }

  ngOnInit() {
    this.getLoanPurposeAndSector()
  }

  async getLoanPurposeAndSector() {
    this.loading = true
    try {
      const [loanPurposeRes, sectorRes]: any = await Promise.all([
        this.getLoanPurpose(),
        this.getSector()
      ])

      this.loanPurposeOptions = loanPurposeRes.map((item: any) => ({ label: item.loanPurposeDesc, value: item.loanPurposeId })),
      this.sectorOptions = sectorRes.map((item: any) => ({ label: item.loanSectorName, value: item.loanSectorId }))
    } catch(err) {
      this.toastr.error(err.message)
    }
    this.loading = false
  }

  getLoanPurpose() {
    return this.api.get("api/loan/loan_purpose")
  }

  getSector() {
    return this.api.get("api/loan/loan_sector")
  }

  async getLoanPurposeDocs(loan_purpose_id: number) {
    this.loading = true

    try {
      const data: any = await this.api.get("api/loan/loan_purpose_docs", { loan_purpose_id })
      this.loanPurposeDocuments = data
      this.enquireDocuments = Array(data.length).fill(null)
    } catch(err) {
      this.toastr.error(err.message)
    }

    this.loading = false
  }

  async getSubSector(loan_sector_id: number) {
    this.loading = true
    try {
      const data: any = await this.api.get("api/loan/loan_subsector", { loan_sector_id })
      this.subSectorOptions = data.map((item: any) => ({ value: item.loanSubSectorId, label: item.loanSubSectorName }))
    } catch(err) {
      this.toastr.error(err.message)
    }
    this.loading = false
  }

  setLoanPurposeId(value: number) {
    if (value === this.loanPurposeId) return

    this.loanPurposeId = Number(value)
    this.getLoanPurposeDocs(this.loanPurposeId)
  }

  setSectorId(value: number) {
    if (value === this.sectorId) return

    this.sectorId = Number(value)
    this.getSubSector(this.sectorId)
  }

  setSubSectorId(value: number) {
    if (value === this.subSectorId) return
    this.subSectorId = Number(value)
  }

  setCurrencyCode(value: string) {
    if (value === this.currencyCode) return
    this.currencyCode = value
  }

  setSex(value: string) {
    if (value === this.sex) return
    this.sex = value
  }

  renderDate(date: string) {
    if (!date) return "---"
    try {
      return this.converter.formatDate(date)
    } catch {
      return "---"
    }
  }

  pickDocument(index: number, docTypeId: number) {
    const input = document.createElement("input")
    input.type = "file"
    input.id = fileInputId
    input.classList.add("d-none")
    const label = document.createElement("label")
    label.htmlFor = fileInputId
    document.body.append(input)
    document.body.append(label)
    input.onchange = (e: any) => {
      try {
        e.preventDefault()
        const file = e.target.files[0]

        if (!file.size || file.size >= FILE_SIZE_LIMIT) throw new Error(this.translateService.instant("LoanEnquire.FileOverLimit"))

        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => {
          console.log(reader.result)

          const commaIndex = (reader.result as string).indexOf(",")
          const base64Content = reader.result.slice(commaIndex + 1)
          // const base64Content = btoa(reader.result as string)
          const newDocuments = this.enquireDocuments.slice()
          newDocuments[index] = {
            docTypeId,
            fileSize: file.size,
            fileName: file.name,
            filePath: file.uri,
            fileBodyString: base64Content as string
          }

          const newLoanPurposeDocuments = this.loanPurposeDocuments.slice()
          if (docTypeId === DOCUMENT_OTHER_TYPE_ID) {
            const documentOtherType = this.loanPurposeDocuments.find(item => item.loanDocId === DOCUMENT_OTHER_TYPE_ID)
            documentOtherType && newLoanPurposeDocuments.push(documentOtherType)
          }

          this.enquireDocuments = newDocuments
          this.loanPurposeDocuments = newLoanPurposeDocuments

          input.remove()
          label.remove()
        }
      } catch(err) {
        this.toastr.error(err.message)
        input.remove()
        label.remove()
      }
    }
    label.click()
  } 

  removeDocument(e: any, index: number) {
    e.preventDefault()
    e.stopPropagation()
    const newDocuments = this.enquireDocuments.slice()
    newDocuments[index] = null
    this.enquireDocuments = newDocuments
  }

  getSectorName() {
    const sector = this.sectorOptions.find(item => item.value === this.sectorId)
    return sector ? sector.label : ""
  }

  getSubSectorName() {
    const subSector = this.subSectorOptions.find(item => item.value === this.subSectorId)
    return subSector ? subSector.label : ""
  }

  validateEnquireDocument() {
    try {
      if (!this.enquireDocuments) throw new Error()
      const purposeDocumentIds = this.loanPurposeDocuments.map(item => item.loanDocId).filter(item => item !== DOCUMENT_OTHER_TYPE_ID)
      for (const id of purposeDocumentIds) {
        const document = this.enquireDocuments.find(item => item && item.docTypeId === id)
        if (!document) throw new Error()
      }

      return true
    } catch {
      return false
    }
  }

  validate(noAlert: boolean = false) {
    try {
      if (!this.loanPurposeId) throw new Error(this.translateService.instant("LoanEnquire.PleaseSelectLoanPurpose"))
      if (!this.amount || Number(this.amount) <= 0) throw new Error(this.translateService.instant("LoanEnquire.AmountGreater"))
      if (!this.currencyCode) throw new Error(this.translateService.instant("LoanEnquire.InvalidCurrencyCode"))
      if (!this.sectorId) throw new Error(this.translateService.instant("LoanEnquire.PleaseSelectSector"))
      if (!this.subSectorId) throw new Error(this.translateService.instant("LoanEnquire.PleaseSelectSubSector"))
      if (!this.sex) throw new Error(this.translateService.instant("LoanEnquire.PleaseSelectSex"))
      if (!this.validateEnquireDocument()) throw new Error(this.translateService.instant("LoanEnquire.PleaseSelectDocuments")) 

      return true
    } catch(err) {
      !noAlert && this.toastr.error(err.message)
      return false
    }
  }

  async submit() {
    const passed = this.validate()
    if (!passed) return 

    this.loading = true

    try {
      const {
        loanPurposeId,
        amount,
        currencyCode,
        sex,
        enquireDocuments
      } = this

      const data: LoanEnquireRequest = {
        loanEnquireId: 0,
        loanPurposeId,
        amount: Number(amount),
        currencyCode,
        sector: this.getSectorName(),
        subSector: this.getSubSectorName(),
        sex,
        enquireDocuments: enquireDocuments.filter(item => !!item) as Document[]
      }

      await this.api.post("api/loan/loan_enquire", data)
      this.toastr.success(this.translateService.instant("LoanEnquire.CreateLoanEnquireSuccess"))
      this.router.navigateByUrl("/loan-enquires")
    } catch(err) {
      if (err) {
        this.toastr.error(err.message)
      } else {
        this.toastr.success(this.translateService.instant("LoanEnquire.CreateLoanEnquireSuccess"))
        this.router.navigateByUrl("/loan-enquires")
      }
    }

    this.loading = false
  }
}